import React from 'react';
import { inject, observer } from 'mobx-react';
import { Alert, Intent } from '@blueprintjs/core';
import { observable } from 'mobx';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import Toast from 'helpers/toaster';
import moment from 'moment';
import VacationModal from '../User/VacationModal';
import { currentTime } from 'helpers/time';
import * as DataList from 'components/DataList';
import { When } from 'components/Tables/ScheduleList';

// Column and card layouts, shared with the page's loading skeleton.
export const VACATION_COLUMNS = [
  { area: 'dates', label: 'Dates', width: 'minmax(170px, 1fr)', bone: 'short', icon: 'calendar' },
  { area: 'technician', label: 'Technician', width: 'minmax(200px, 1.3fr)', bone: 'person' },
  { area: 'status', label: 'Status', width: '120px', bone: 'pill' },
  { area: 'approved', label: 'Approved by', width: 'minmax(140px, 1fr)', bone: 'single' },
  { area: 'notes', label: 'Notes', width: 'minmax(160px, 1.4fr)', bone: 'single', icon: 'comment' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const VACATION_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'technician status actions',
    'dates dates dates',
    'notes notes notes',
    'approved approved approved',
  ],
  footer: ['approved'],
};

export const VACATION_MIN_TABLE_WIDTH = 900;

const VACATION_FILTERS = {
  user_id: 'technician',
};

// VacationModal offers approved / rejected; anything else is still waiting.
const STATUS_TONES = {
  approved: 'green',
  pending: 'amber',
  rejected: 'red',
  declined: 'red',
};

const VacationStatus = ({ status }) => (
  <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>
    {status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : 'No status'}
  </DataList.Pill>
);

const dateLabel = (date) => {
  const time = currentTime(date);
  return time.format(time.isSame(currentTime(), 'year') ? 'MMM D' : 'MMM D, YYYY');
};

@inject('userStore', 'viewStore') @observer
class VacationsTable extends React.Component {

  @observable isOpenVacationModal = false;
  @observable isDeleteDialogVisible = false;
  @observable vacation;
  @observable edit = false;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        vacationsSearch
      }
    } = this.props;
    this.props.viewStore.vacationsSearch = {
      ...vacationsSearch,
      page: newPage
    };
    this.props.viewStore.usersVacations();
  }

  // Keep the date range, drop the technician filter, and remount the
  // filter bar so the picker forgets its selection.
  clearFilters = () => {
    const { viewStore } = this.props;
    const { vacationsSearch } = viewStore;
    const { user_id, ...where } = vacationsSearch.where || {};
    viewStore.vacationsSearch = {
      ...vacationsSearch,
      where,
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.usersVacations();
  }

  handleVacationClick(event, vacation) {
    switch (event.label) {
      case 'Edit':
        this.vacation = vacation;
        this.props.userStore.vacation.object = this.vacation.attributes;
        this.edit = true
        this.isOpenVacationModal = true
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.vacation = vacation;
        break;
      default:
    }
  }

  handleCloseVacationModal() {
    this.isOpenVacationModal = false
    this.edit = false;
    this.vacation = null;
    this.props.userStore.vacation.object = {
      start_date: moment(),
      end_date: moment()
    }
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.userStore.deleteVacation(this.vacation)
      .then(this.onSuccessfulDeleteVacation.bind(this))
      .catch(this.onFailedVacationDelete.bind(this))
  }

  onSuccessfulDeleteVacation(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Vacation deleted successfully!', intent: Intent.SUCCESS })
    this.vacation = null;
    this.isDeleteDialogVisible = false;
    this.props.userStore.loadUsersVacations();
  }

  onFailedVacationDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete vacation!', intent: Intent.DANGER })
  }

  renderEmpty() {
    const { viewStore } = this.props;
    const { rangeStart, rangeEnd, where } = viewStore.vacationsSearch;
    const range = rangeStart && rangeEnd ? `${dateLabel(rangeStart)} and ${dateLabel(rangeEnd)}` : null;
    const filters = DataList.activeFilters(where, VACATION_FILTERS);
    const onlyDates = filters.length === 0 && range;
    return (
      <DataList.Empty
        noun="vacations"
        filters={filters}
        title={onlyDates ? `No vacations between ${range}` : undefined}
        message={onlyDates ? 'Nobody has time off in this date range. Pick other dates above.' : undefined}
        onClearFilters={this.clearFilters}
      />
    );
  }

  render() {
    let {
      viewStore,
      userStore: {
        vacations: {
          objects,
          meta
        }
      }
    } = this.props;
    if (objects.length === 0) return this.renderEmpty();

    const count = meta.total_records || objects.length;

    return(
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group responsive-pagination bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={VACATION_COLUMNS}
          cards={VACATION_CARDS}
          minTableWidth={VACATION_MIN_TABLE_WIDTH}
          total={count}
          noun="vacations"
          note={DataList.sortNote(viewStore.vacationsSearch.order)}
        >
          {objects.map((vacation) => {
            const attributes = vacation.attributes;
            const name = attributes.full_name;
            const approvedBy = attributes.created_by_fullname;
            return (
              <DataList.Row key={vacation.id}>
                <DataList.Cell area="dates">
                  <When start={attributes.start_date} end={attributes.end_date} />
                </DataList.Cell>
                <DataList.Cell area="technician">
                  {name
                    ? <DataList.Person name={name} />
                    : <DataList.Muted>No technician</DataList.Muted>}
                </DataList.Cell>
                <DataList.Cell area="status">
                  <VacationStatus status={attributes.status} />
                </DataList.Cell>
                <DataList.Cell area="approved">
                  {approvedBy ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Approved by </DataList.CardOnly>{approvedBy}</React.Fragment>}
                      title={approvedBy}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="notes">
                  {attributes.notes ? (
                    <span className="dl-muted dl-truncate" title={attributes.notes}>
                      {attributes.notes}
                    </span>
                  ) : (
                    <DataList.Muted>No notes</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit vacation', onClick: () => this.handleVacationClick({ label: 'Edit' }, vacation) },
                      { icon: 'trash', label: 'Delete vacation', intent: 'danger', onClick: () => this.handleVacationClick({ label: 'Delete' }, vacation) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <VacationModal
          isOpen={this.isOpenVacationModal}
          onClose={this.handleCloseVacationModal.bind(this)}
          user={this.vacation && this.vacation.attributes.user}
          vacation={this.edit ? this.vacation.attributes : null}
        />

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to delete this vacation{this.vacation && this.vacation.attributes.full_name ? <React.Fragment> for <b>{this.vacation.attributes.full_name}</b></React.Fragment> : null}?
          </p>
        </Alert>
      </React.Fragment>
    )
  }

};

export default VacationsTable;
